/**
 * Module introduction component.
 *
 * Displays module introduction screen before starting each exam module.
 * Shows module title, description, instructions, and countdown timer or audio waveform.
 * For audio-based introductions (material_type === "audio" and duration === null),
 * displays an animated waveform and automatically plays the audio. The module starts
 * automatically when the audio completes. Data is fetched from database.
 *
 * @module ModuleIntroduction
 */

import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Play, Book, Pen, Headphones, Mic, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { AudioWaveform } from "@/components/exam/AudioWaveform";
import { useAudioAnalyser } from "@/hooks/useAudioAnalyser";
import { buildMediaSource } from "@/utils/examSimulation";
import type {
  ExamModule,
  ModuleIntroduction as ModuleIntroductionType,
} from "@/types/exam-simulation";

interface ModuleIntroductionProps {
  readonly module: ExamModule;
  readonly introduction: ModuleIntroductionType | null;
  readonly onStart: () => void;
  readonly defaultDuration?: number; // in seconds, default 30 seconds
}

const moduleIcons = {
  reading: Book,
  writing: Pen,
  listening: Headphones,
  speaking: Mic,
};

const moduleColors = {
  reading: "text-exam-focus-border",
  writing: "text-exam-success",
  listening: "text-exam-audio-wave",
  speaking: "text-exam-recording",
};

const moduleBgColors = {
  reading: "bg-exam-focus-border/20",
  writing: "bg-exam-success/20",
  listening: "bg-exam-audio-wave/20",
  speaking: "bg-exam-recording/20",
};

export function ModuleIntroduction({
  module,
  introduction,
  onStart,
  defaultDuration = 30, // 30 seconds default
}: ModuleIntroductionProps) {
  const { t } = useTranslation();
  // Determine if this is an audio-based introduction
  const isAudioBased =
    introduction?.material_type === "audio" && introduction?.duration === null;

  // Use duration from database, or default if not provided (only for timer-based)
  const duration =
    introduction?.duration ?? (isAudioBased ? null : defaultDuration);
  const [timeRemaining, setTimeRemaining] = useState(duration ?? 0);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [needsManualPlay, setNeedsManualPlay] = useState(false);
  const [audioEl, setAudioEl] = useState<HTMLAudioElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Drive the intro waveform with the real playing audio (flat during silence)
  const { readInto: readAudioLevels } = useAudioAnalyser({
    mediaElement: audioEl,
    enabled: isAudioPlaying,
  });

  const Icon = moduleIcons[module];
  const iconColor = moduleColors[module];
  const bgColor = moduleBgColors[module];

  // Get title, description, and instructions from database only
  const title = introduction?.title || "";
  const instructions = introduction?.text
    ? introduction.text.split("\n").filter((line) => line.trim())
    : [];

  // Handle audio-based introduction
  useEffect(() => {
    if (!isAudioBased || !introduction) return;

    // Prefer material_binary if available (already loaded), otherwise use material_url
    const audioSrc = buildMediaSource(
      introduction.material_url,
      introduction.material_binary,
      introduction.material_mime_type,
      "audio/mpeg",
    );

    if (!audioSrc) {
      // Audio source not available, allow manual start
      logger.warn("No audio source available", {
        material_url: introduction.material_url,
        has_material_binary: !!introduction.material_binary,
      });
      return;
    }

    // Create audio element
    const audio = new Audio(audioSrc);
    audioRef.current = audio;
    setAudioEl(audio);

    // Set audio attributes for better compatibility
    audio.preload = "auto";
    audio.volume = 1;

    // Handle audio events
    const handlePlay = () => {
      setIsAudioPlaying(true);
      logger.info("Audio started playing");
    };

    const handlePause = () => {
      setIsAudioPlaying(false);
      logger.info("Audio paused");
    };

    const handleEnded = () => {
      setIsAudioPlaying(false);
      logger.info("Audio finished, starting module");
      onStart();
    };

    const handleError = (event: Event) => {
      setIsAudioPlaying(false);
      setAudioError(true);
      const error = event.target as HTMLAudioElement;
      logger.error("Audio error", error.error, {
        code: error.error?.code,
        message: error.error?.message,
        src: audioSrc.substring(0, 50) + "...",
      });
      // On error, allow manual start - don't block the user
    };

    const handleCanPlay = async () => {
      logger.info("Audio can play, attempting autoplay");
      // Try to play when audio is ready
      try {
        await audio.play();
      } catch (error) {
        logger.warn("Audio autoplay failed", { error });
        // Auto-play may fail due to browser policies
        // Show manual play option
        setNeedsManualPlay(true);
        setIsAudioPlaying(false);
      }
    };

    const handleLoadStart = () => {
      logger.info("Audio loading started");
    };

    const handleLoadedData = () => {
      logger.info("Audio data loaded");
    };

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("loadstart", handleLoadStart);
    audio.addEventListener("loadeddata", handleLoadedData);

    // Load the audio (this will trigger canplay event)
    audio.load();

    // Cleanup
    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("loadstart", handleLoadStart);
      audio.removeEventListener("loadeddata", handleLoadedData);
      audio.pause();
      audio.src = ""; // Clear source to stop loading
      audioRef.current = null;
      setAudioEl(null);
    };
  }, [isAudioBased, introduction, onStart]);

  // Handle timer-based introduction
  useEffect(() => {
    if (isAudioBased) return; // Skip timer for audio-based introductions

    if (timeRemaining <= 0) {
      // Use setTimeout to avoid setState during render warning
      const timeoutId = setTimeout(() => {
        onStart();
      }, 0);
      return () => clearTimeout(timeoutId);
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, onStart, isAudioBased]);

  const handleManualPlay = async () => {
    if (!audioRef.current) return;
    try {
      await audioRef.current.play();
      setNeedsManualPlay(false);
    } catch (error) {
      logger.error("Manual play failed", error);
      setAudioError(true);
    }
  };

  const handleStartEarly = () => {
    // Stop audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setAudioEl(null);
      setIsAudioPlaying(false);
    }
    onStart();
  };

  const handleExtendTime = () => {
    setTimeRemaining((prev) => prev + 300); // +5 minutes
  };

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;
  const isLowTime = timeRemaining < 10; // Less than 10 seconds

  const renderAudioStatus = () => {
    if (needsManualPlay) {
      return (
        <div className="space-y-4">
          <AudioWaveform isPlaying={false} />
          <Button
            onClick={handleManualPlay}
            size="lg"
            className="px-8 py-5 text-base font-semibold"
          >
            <Play className="w-4 h-4 mr-2" />
            Play Audio
          </Button>
          <p className="text-xs text-muted-foreground">
            Click to start the audio introduction
          </p>
        </div>
      );
    }

    if (audioError) {
      return (
        <div className="space-y-4">
          <AudioWaveform isPlaying={false} />
          <p className="text-xs text-red-500">
            {t("simulation.active.audioError")}
          </p>
        </div>
      );
    }

    return (
      <>
        <AudioWaveform isPlaying={isAudioPlaying} getLevels={readAudioLevels} />
        <p className="text-xs text-muted-foreground mt-3">
          {t("simulation.active.autoStartAudio")}
        </p>
      </>
    );
  };

  let audioStatusText = "Loading audio...";
  if (needsManualPlay) {
    audioStatusText = "Ready to play";
  } else if (isAudioPlaying) {
    audioStatusText = "Playing audio";
  }

  return (
    <div className="flex min-h-[100dvh] items-center justify-center overflow-y-auto bg-background p-3 sm:p-4">
      <div className="flex min-h-fit w-full max-w-3xl flex-col justify-center py-4 sm:py-8">
        <div className="text-center mb-4">
          <div className="flex justify-center mb-4">
            <div
              className={cn(
                "w-20 h-20 rounded-full flex items-center justify-center",
                bgColor,
              )}
            >
              <Icon className={cn("w-10 h-10", iconColor)} />
            </div>
          </div>
          {title && (
            <h1 className="text-3xl font-bold text-foreground mb-2">{title}</h1>
          )}
          {introduction?.subtitle && (
            <p className="text-muted-foreground text-base mb-2">
              {introduction.subtitle}
            </p>
          )}
        </div>

        {instructions.length > 0 && (
          <div className="mb-4 rounded-2xl border-2 border-border bg-card p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-foreground mb-3">
              {t("simulation.moduleIntroduction.instructionsHeading", {
                defaultValue: "Instructions",
              })}
            </h2>
            <ul className="space-y-2">
              {instructions.map((instruction, index) => (
                <li
                  key={`instruction-${index}-${instruction.slice(0, 20)}`}
                  className="flex items-start gap-2"
                >
                  <span className="text-primary font-semibold mt-0.5 text-sm">
                    •
                  </span>
                  <span className="text-sm text-foreground leading-relaxed flex-1">
                    {instruction}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-4 rounded-2xl border-2 border-border bg-card p-4 sm:p-6">
          <div className="text-center">
            {isAudioBased ? (
              <>
                <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wider">
                  {audioStatusText}
                </p>
                {renderAudioStatus()}
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wider">
                  {t("simulation.active.startingIn")}
                </p>
                <div className="flex items-center justify-center">
                  <span
                    className={cn(
                      "font-bold text-5xl tabular-nums tracking-tight",
                      isLowTime ? "text-exam-timer-warning" : "text-foreground",
                    )}
                  >
                    {String(minutes).padStart(2, "0")}:
                    {String(seconds).padStart(2, "0")}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mb-2 flex flex-col justify-center gap-3 sm:flex-row">
          <Button
            onClick={handleStartEarly}
            size="lg"
            className="px-8 py-5 text-base font-semibold bg-primary hover:bg-primary/90"
          >
            <Play className="w-4 h-4 mr-2" />
            {t("simulation.active.startEarly")}
          </Button>
          {!isAudioBased && (
            <Button
              onClick={handleExtendTime}
              size="lg"
              variant="outline"
              className="px-6 py-5 text-base font-semibold"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t("simulation.active.add5MinLabel")}
            </Button>
          )}
        </div>

        {!isAudioBased && (
          <p className="text-center text-xs text-muted-foreground">
            {t("simulation.active.autoStart")}
          </p>
        )}
      </div>
    </div>
  );
}
